<template>
        <div class="p-6">
                <div class="mb-4">
                        <router-link
                                to="/discount"
                                class="inline-flex items-center gap-2 text-gray-500 no-underline text-sm transition-colors hover:text-orange-7"
                        >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Discounts
                        </router-link>
                </div>

                <div class="flex justify-between items-start mb-6 flex-wrap gap-4">
                        <div>
                                <h1 class="text-2xl font-semibold text-gray-800 m-0">
                                        {{ loading ? 'Loading...' : `Edit: ${discount?.name || ''}` }}
                                </h1>
                                <p class="text-gray-500 mt-1 mb-0">Configure discount properties and vouchers</p>
                        </div>
                        <button
                                v-if="activeTab === 'properties'"
                                :disabled="!hasChanges || saving"
                                class="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                @click="saveChanges"
                        >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                                {{ saving ? 'Saving...' : 'Save Changes' }}
                        </button>
                </div>

                <div v-if="loading" class="flex justify-center items-center p-12">
                        <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-7 rounded-full animate-spin"></div>
                </div>

                <div v-else-if="discount" class="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div class="flex border-b border-gray-200">
                                <button
                                        v-for="tab in tabs"
                                        :key="tab.id"
                                        class="px-6 py-4 text-sm font-medium border-none bg-transparent cursor-pointer transition-all relative -mb-px"
                                        :class="activeTab === tab.id ? 'text-orange-7 border-b-2 border-orange-7' : 'text-gray-500 border-b-2 border-transparent'"
                                        @click="activeTab = tab.id"
                                >
                                        {{ tab.label }}
                                </button>
                        </div>

                        <div v-if="activeTab === 'properties'" class="p-6">
                                <div class="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6">
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                                <input
                                                        v-model="discount.name"
                                                        type="text"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border focus:border-orange-7"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Tracker</label>
                                                <select
                                                        v-model="discount.trackerid"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none bg-white box-border"
                                                >
                                                        <option :value="0">-- None --</option>
                                                        <option v-for="tracker in trackers" :key="tracker.id" :value="tracker.id">{{ tracker.name }}</option>
                                                </select>
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Referral Program</label>
                                                <select
                                                        v-model="discount.referralprogramid"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none bg-white box-border"
                                                >
                                                        <option :value="null">-- None --</option>
                                                        <option v-for="program in referralPrograms" :key="program.id" :value="program.id">{{ program.name }}</option>
                                                </select>
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Relative %</label>
                                                <input
                                                        v-model.number="discount.relative"
                                                        type="number"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Absolute (cents)</label>
                                                <input
                                                        v-model.number="discount.absolute"
                                                        type="number"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                                                <input
                                                        v-model="discount.currency"
                                                        type="text"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                                                <input
                                                        v-model.number="discount.quantity"
                                                        type="number"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Limit</label>
                                                <input
                                                        v-model.number="discount.limit"
                                                        type="number"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Extra Pages</label>
                                                <input
                                                        v-model.number="discount.extrapages"
                                                        type="number"
                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                >
                                        </div>
                                </div>

                                <div class="mt-6 pt-6 border-t border-gray-200">
                                        <h3 class="text-sm font-semibold text-gray-700 mb-4 mt-0">Shipping Options</h3>
                                        <div class="flex flex-wrap gap-6">
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="discount.freeshipping"
                                                                type="checkbox"
                                                                :true-value="1"
                                                                :false-value="0"
                                                                class="w-[1.125rem] h-[1.125rem] accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">Free Shipping</span>
                                                </label>
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="discount.freehandling"
                                                                type="checkbox"
                                                                :true-value="1"
                                                                :false-value="0"
                                                                class="w-[1.125rem] h-[1.125rem] accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">Free Handling</span>
                                                </label>
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="discount.single"
                                                                type="checkbox"
                                                                :true-value="1"
                                                                :false-value="0"
                                                                class="w-[1.125rem] h-[1.125rem] accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">Single Use</span>
                                                </label>
                                        </div>
                                </div>
                        </div>

                        <div v-if="activeTab === 'vouchers'" class="p-6">
                                <div class="flex justify-between items-center mb-4 flex-wrap gap-4">
                                        <h3 class="text-base font-semibold text-gray-700 m-0">Voucher Codes</h3>
                                        <div class="flex gap-2">
                                                <button
                                                        class="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer"
                                                        @click="exportVouchers"
                                                >
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                        Export
                                                </button>
                                                <button
                                                        class="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer"
                                                        @click="showVoucherForm = !showVoucherForm"
                                                >
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        </svg>
                                                        Add Voucher
                                                </button>
                                        </div>
                                </div>

                                <div
                                        v-if="showVoucherForm"
                                        class="mb-6 p-4 bg-gray-50 rounded-md border border-dashed border-gray-300"
                                >
                                        <h4 class="text-sm font-semibold text-gray-700 mb-4 mt-0">Create New Voucher</h4>
                                        <div class="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4 mb-4">
                                                <div>
                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Code</label>
                                                        <input
                                                                v-model="newVoucher.code"
                                                                type="text"
                                                                placeholder="VOUCHER123"
                                                                class="w-full px-2 py-1.5 border border-gray-300 rounded text-sm box-border"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Limit</label>
                                                        <input
                                                                v-model.number="newVoucher.limit"
                                                                type="number"
                                                                placeholder="1"
                                                                class="w-full px-2 py-1.5 border border-gray-300 rounded text-sm box-border"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                                                        <input
                                                                v-model="newVoucher.startdate"
                                                                type="date"
                                                                class="w-full px-2 py-1.5 border border-gray-300 rounded text-sm box-border"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                                                        <input
                                                                v-model="newVoucher.enddate"
                                                                type="date"
                                                                class="w-full px-2 py-1.5 border border-gray-300 rounded text-sm box-border"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        class="px-3 py-2 bg-orange-7 text-white border-none rounded text-sm cursor-pointer"
                                                        @click="createVoucher"
                                                >
                                                        Create
                                                </button>
                                                <button
                                                        class="px-3 py-2 bg-white border border-gray-300 rounded text-sm cursor-pointer"
                                                        @click="showVoucherForm = false"
                                                >
                                                        Cancel
                                                </button>
                                        </div>
                                </div>

                                <div v-if="vouchers.length === 0" class="text-center p-8 text-gray-500 bg-gray-50 rounded-md">
                                        <p class="m-0">No voucher codes created yet.</p>
                                </div>

                                <table v-else class="w-full border-collapse">
                                        <thead>
                                                <tr class="bg-gray-50 border-b border-gray-200">
                                                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">ID</th>
                                                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                                                        <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Claimed</th>
                                                        <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Used</th>
                                                        <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Limit</th>
                                                        <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Valid</th>
                                                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Start</th>
                                                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">End</th>
                                                </tr>
                                        </thead>
                                        <tbody>
                                                <tr
                                                        v-for="voucher in vouchers"
                                                        :key="voucher.id"
                                                        class="border-b border-gray-200"
                                                >
                                                        <td class="px-4 py-3 text-sm text-gray-500">{{ voucher.id }}</td>
                                                        <td class="px-4 py-3 text-sm font-medium text-gray-700">{{ voucher.code }}</td>
                                                        <td class="px-4 py-3 text-center text-sm text-gray-700">{{ voucher.claimed }}</td>
                                                        <td class="px-4 py-3 text-center text-sm text-gray-700">{{ voucher.used }}</td>
                                                        <td class="px-4 py-3 text-center text-sm text-gray-700">{{ voucher.limit }}</td>
                                                        <td class="px-4 py-3 text-center">
                                                                <span
                                                                        class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
                                                                        :class="voucher.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                                                                >
                                                                        {{ voucher.valid ? 'Yes' : 'No' }}
                                                                </span>
                                                        </td>
                                                        <td class="px-4 py-3 text-sm text-gray-500">{{ voucher.startdate || '-' }}</td>
                                                        <td class="px-4 py-3 text-sm text-gray-500">{{ voucher.enddate || '-' }}</td>
                                                </tr>
                                        </tbody>
                                </table>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import DiscountService from 'services/DiscountService';
import DiscountVoucherService from 'services/DiscountVoucherService';
import AffiliateService from 'services/AffiliateService';
import ReferralProgramService from 'services/ReferralProgramService';

export default defineComponent({
        name: 'EditDiscountPage',
        setup() {
                const route = useRoute();
                const auth0 = useAuth0();

                const discount = ref(null);
                const originalDiscount = ref(null);
                const vouchers = ref([]);
                const trackers = ref([]);
                const referralPrograms = ref([]);
                const loading = ref(true);
                const saving = ref(false);
                const activeTab = ref('properties');
                const showVoucherForm = ref(false);
                const newVoucher = ref({
                        code: '',
                        limit: 1,
                        startdate: '',
                        enddate: '',
                });

                const tabs = [
                        { id: 'properties', label: 'Properties' },
                        { id: 'vouchers', label: 'Vouchers' },
                ];

                const loggedIn = computed(() => auth0.isAuthenticated.value);

                const hasChanges = computed(() => {
                        if (!discount.value || !originalDiscount.value) return false;
                        return JSON.stringify(discount.value) !== JSON.stringify(originalDiscount.value);
                });

                const loadData = async () => {
                        if (!loggedIn.value) return;
                        loading.value = true;
                        try {
                                const discountId = route.params.id;
                                const params = new URLSearchParams({ limit: '0' });
                                const [discountData, vouchersData, trackersData, programsData] = await Promise.all([
                                        DiscountService.get(discountId),
                                        DiscountVoucherService.getByDiscount(discountId, params),
                                        AffiliateService.getAll(params),
                                        ReferralProgramService.getAll(params),
                                ]);
                                discount.value = { ...discountData };
                                originalDiscount.value = { ...discountData };
                                vouchers.value = vouchersData || [];
                                trackers.value = trackersData || [];
                                referralPrograms.value = programsData || [];
                        } catch (error) {
                                console.error('Failed to load discount:', error);
                        } finally {
                                loading.value = false;
                        }
                };

                const saveChanges = async () => {
                        if (!hasChanges.value) return;
                        saving.value = true;
                        try {
                                await DiscountService.update(discount.value.id, discount.value);
                                originalDiscount.value = { ...discount.value };
                        } catch (error) {
                                console.error('Failed to save discount:', error);
                        } finally {
                                saving.value = false;
                        }
                };

                const createVoucher = async () => {
                        try {
                                const voucherData = {
                                        ...newVoucher.value,
                                        discountid: discount.value.id,
                                };
                                await DiscountVoucherService.create(voucherData);
                                const params = new URLSearchParams({ limit: '0' });
                                vouchers.value = await DiscountVoucherService.getByDiscount(discount.value.id, params);
                                showVoucherForm.value = false;
                                newVoucher.value = { code: '', limit: 1, startdate: '', enddate: '' };
                        } catch (error) {
                                console.error('Failed to create voucher:', error);
                        }
                };

                const exportVouchers = () => {
                        const csv = vouchers.value.map(v => `${v.id},${v.code},${v.claimed},${v.used},${v.limit},${v.valid},${v.startdate || ''},${v.enddate || ''}`).join('\n');
                        const header = 'ID,Code,Claimed,Used,Limit,Valid,Start,End\n';
                        const blob = new Blob([header + csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `vouchers-${discount.value.id}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                };

                watch(loggedIn, (isLoggedIn) => {
                        if (isLoggedIn) loadData();
                }, { immediate: true });

                return {
                        discount,
                        vouchers,
                        trackers,
                        referralPrograms,
                        loading,
                        saving,
                        activeTab,
                        tabs,
                        hasChanges,
                        showVoucherForm,
                        newVoucher,
                        saveChanges,
                        createVoucher,
                        exportVouchers,
                };
        },
});
</script>
